import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sideTone, sportsBuyOption } from '@/lib/sports-market';
import type { OrderSide } from '@/analytics';
import type { MarketOptionDto, SportsLineLadderDto, SportsLineOutcomeDto } from '@/lib/models';
import { MarketOutcomePricesSubscriber, useOutcomePrices, type OutcomePriceMap } from '../BinaryMarkets/useOutcomePrices';
import { SportsBlockHeader } from './SportsBlockHeader';
import { SportsOutcomePill } from './SportsOutcomePill';

export interface SportsLineLadderBlockProps {
  /** Section title, e.g. "Spreads" or "Totals". */
  title: string;
  /** The spread/total ladder to render. */
  ladder: SportsLineLadderDto;
  /** Opens the buy-in popup pre-set to the tapped (side + selected line) contract (ARC-80 §7). */
  onOpenBuy: (option: MarketOptionDto, side: OrderSide) => void;
}

// The selector shows 5 line values with the SELECTED one always centred (under the
// pointer), per Figma; offsets from the selected index for the two neighbours each side.
const SELECTOR_OFFSETS = [-2, -1, 0, 1, 2] as const;

/** Over→"O", Under→"U", else the leg's own label (team). Matches the Figma pills. */
function pillLabel(leg: SportsLineOutcomeDto): string {
  if (leg.side === 'OVER') return 'O';
  if (leg.side === 'UNDER') return 'U';
  return leg.label;
}

/** Live-or-seed whole-percent for a leg (live yes-cents keyed by external id). */
function legPercent(leg: SportsLineOutcomeDto, prices: OutcomePriceMap): number | null {
  return prices[leg.externalOutcomeId]?.yesCents ?? leg.chancePercent;
}

/**
 * A spread or total market rendered as a navigable line ladder (ARC sports §detail):
 * a ‹ › line selector seeded at the median (`featuredLine`), and the two pills for
 * the selected line. Live prices overlay the seed via Centrifugo.
 */
export function SportsLineLadderBlock({ title, ladder, onOpenBuy }: SportsLineLadderBlockProps) {
  const { prices, setPrice } = useOutcomePrices();
  const featuredIdx = Math.max(
    0,
    ladder.rungs.findIndex((r) => r.line === ladder.featuredLine),
  );
  const [selected, setSelected] = useState(featuredIdx);

  if (ladder.rungs.length === 0) return null;

  const idx = Math.min(selected, ladder.rungs.length - 1);
  const rung = ladder.rungs[idx];

  return (
    <div className="flex flex-col gap-2">
      <MarketOutcomePricesSubscriber
        marketId={ladder.marketId}
        externalMarketId={ladder.externalMarketId}
        onPrice={setPrice}
      />

      <SportsBlockHeader title={title} volume={ladder.volume} />

      {/* Line selector (Figma 5379:132297): stadium border #e6e1f0, ‹ › ends, the
          selected value centred + a purple down-pointer above it. */}
      <div className="relative flex h-[52px] items-center justify-between rounded-[25px] border-2 border-stroke-soft px-3">
        {/* purple down-pointer flush to the inside of the top border, centred over the selected value */}
        <span
          aria-hidden
          className="absolute left-1/2 top-0 -translate-x-1/2 h-0 w-0 border-x-[6px] border-x-transparent border-t-[7px] border-t-purple"
        />
        <button
          type="button"
          aria-label="Previous line"
          disabled={idx === 0}
          onClick={() => setSelected(idx - 1)}
          className="flex size-6 shrink-0 items-center justify-center text-ink-muted disabled:opacity-30 cursor-pointer"
        >
          <ChevronLeft className="size-5" />
        </button>
        <div className="flex flex-1 items-center">
          {SELECTOR_OFFSETS.map((off) => {
            const r = ladder.rungs[idx + off];
            if (!r) return <span key={off} className="flex-1" aria-hidden />;
            const active = off === 0;
            return (
              <button
                key={off}
                type="button"
                onClick={() => setSelected(idx + off)}
                className={cn(
                  'flex-1 cursor-pointer border-none bg-transparent text-center',
                  active ? 'text-[18px] font-semibold text-ink' : 'text-[16px] text-ink-muted',
                )}
              >
                {r.line}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label="Next line"
          disabled={idx === ladder.rungs.length - 1}
          onClick={() => setSelected(idx + 1)}
          className="flex size-6 shrink-0 items-center justify-center text-ink-muted disabled:opacity-30 cursor-pointer"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {/* The two pills for the selected line */}
      <div className="grid grid-cols-2 gap-2">
        {rung.outcomes.map((leg) => (
          <SportsOutcomePill
            key={leg.outcomeId}
            variant="line"
            tone={sideTone(leg.side)}
            label={leg.shortName || pillLabel(leg)}
            lineLabel={leg.line}
            percent={legPercent(leg, prices)}
            onClick={() => onOpenBuy(sportsBuyOption(leg, ladder), 'yes')}
          />
        ))}
      </div>
    </div>
  );
}
